import { useState } from "react";
import { useNavigate } from "react-router";
import {
  useScheduledJobs, useScheduledJob, useCreateScheduledJob,
  useUpdateScheduledJob, useDeleteScheduledJob, useTriggerScheduledJob,
  usePauseScheduledJob, usePlatformEnterprises, useFiles,
} from "@platform/hooks";
import { useAuthStore } from "../../stores/authStore";
import { PageHeader } from "../../components/shared/PageHeader";
import { Button, Input, Label, useToast } from "@nthamizh/ui";
import { Plus, Play, Pencil, Trash2, ChevronDown, Clock, History, Pause, PlayCircle } from "lucide-react";
import type { SchedulerJob } from "@platform/api-client";

// ── Application registry ────────────────────────────────────────────────────
// Maps the UI-facing application name to the internal webhook URL.
// Adding a new application only requires adding an entry here — no
// other code changes needed. The URL is resolved server-side and
// never shown to the user.
const APP_REGISTRY: Record<string, {
  label: string;
  webhookUrl: string;
  etlTables?: string[];
}> = {
  costsimulator: {
    label: "Cost Simulator",
    webhookUrl: "http://costsim-api:4002/v1/jobs/etl-handler",
    etlTables: [
      "list_of_values",
      "valid_combinations",
      "eligibility",
      "department",
      "person",
      "person_element",
      "position",
      "job",
      "payroll",
      "fast_formula",
      "iac_ppg",
      "iac_seg",
    ],
  },
  configiq: {
    label: "ConfigIQ",
    webhookUrl: "http://configiq-api:4001/v1/jobs",
    etlTables: [],
  },
};

const TABLE_LABELS: Record<string, string> = {
  list_of_values:    "List of Values",
  valid_combinations:"Valid Combinations",
  eligibility:       "Eligibility Costing",
  department:        "Department Costing",
  person:            "Person Costing",
  person_element:    "Person Element Costing",
  position:          "Position Costing",
  job:               "Job Costing",
  payroll:           "Payroll Costing",
  fast_formula:      "Fast Formula Overrides",
  iac_ppg:           "IAC PPG Overrides",
  iac_seg:           "IAC Segment Overrides",
};

// ── Utilities ────────────────────────────────────────────────────────────────

const JOB_TYPE_LABEL: Record<SchedulerJob["jobType"], string> = {
  etl: "ETL",
  report_generation: "Report generation",
};

const STATUS_STYLE: Record<string, string> = {
  running: "bg-blue-50 text-blue-700 border-blue-100",
  success: "bg-emerald-50 text-emerald-700 border-emerald-100",
  failed:  "bg-red-50 text-red-700 border-red-100",
};

function describeTrigger(job: SchedulerJob): string {
  if (job.triggerType === "cron") return `Cron: ${job.triggerConfig.expression}`;
  if (job.triggerType === "interval") {
    const s = job.triggerConfig.seconds ?? 0;
    if (s % 3600 === 0) return `Every ${s / 3600}h`;
    if (s % 60 === 0)   return `Every ${s / 60}m`;
    return `Every ${s}s`;
  }
  return job.triggerConfig.at ? `Once at ${new Date(job.triggerConfig.at).toLocaleString()}` : "One-time";
}

function formatDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString() : "—";
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function PlatformSchedulerPage() {
  const { toast } = useToast();
  const { data: jobs, isLoading } = useScheduledJobs();
  const deleteJob  = useDeleteScheduledJob();
  const triggerJob = useTriggerScheduledJob();
  const pauseJob   = usePauseScheduledJob();
  const navigate   = useNavigate();
  const [createOpen, setCreateOpen]               = useState(false);
  const [editingJob, setEditingJob]               = useState<SchedulerJob | null>(null);
  const [expandedId, setExpandedId]               = useState<string | null>(null);
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null);

  const handleDelete = async (id: string) => {
    if (confirmingDeleteId !== id) {
      setConfirmingDeleteId(id);
      setTimeout(() => setConfirmingDeleteId((c) => (c === id ? null : c)), 3000);
      return;
    }
    await deleteJob.mutateAsync(id);
    setConfirmingDeleteId(null);
  };

  const handleTrigger = async (id: string) => {
    try {
      await triggerJob.mutateAsync(id);
      toast({ title: "Job triggered", variant: "success" });
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : "Couldn't trigger job", variant: "error" });
    }
  };

  const handlePause = async (job: SchedulerJob) => {
    try {
      await pauseJob.mutateAsync({ id: job.id, enabled: !job.enabled });
      toast({ title: job.enabled ? "Job paused" : "Job resumed", variant: "success" });
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : "Failed", variant: "error" });
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Scheduler"
        subtitle="ETL and report generation jobs — Platform triggers, the owning application does the work."
        action={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate("/platform/scheduler/history")}>
              <History className="w-4 h-4" /> View history
            </Button>
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="w-4 h-4" /> New job
            </Button>
          </div>
        }
      />

      {isLoading && <p className="text-sm text-ds-text-muted">Loading…</p>}

      {!isLoading && (!jobs || jobs.length === 0) && (
        <div className="bg-white rounded-[12px] border border-ds-border p-10 text-center">
          <Clock className="w-8 h-8 text-ds-text-muted mx-auto mb-2" />
          <p className="text-sm text-ds-text-secondary">No scheduled jobs yet.</p>
        </div>
      )}

      {!isLoading && jobs && jobs.length > 0 && (
        <div className="space-y-3">
          {jobs.map((job) => (
            <div key={job.id} className="bg-white rounded-[12px] border border-ds-border overflow-hidden">
              <button
                onClick={() => setExpandedId((id) => (id === job.id ? null : job.id))}
                className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-ds-bg/60 transition-colors"
              >
                {/* Status dot */}
                {(() => {
                  if (!job.enabled) return (
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-400 flex-shrink-0" title="Paused — no future runs will fire" />
                  );
                  if (job.nextRunAt && new Date(job.nextRunAt) > new Date()) return (
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 flex-shrink-0" title={`Next run: ${new Date(job.nextRunAt).toLocaleString()}`} />
                  );
                  return <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" />;
                })()}

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-ds-text">{job.name}</span>
                    <span className="text-[11px] font-medium uppercase tracking-wide px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-600 border border-indigo-100">
                      {JOB_TYPE_LABEL[job.jobType]}
                    </span>
                  </div>
                  <p className="text-xs text-ds-text-muted">
                    {describeTrigger(job)} · Next run: {formatDate(job.nextRunAt)}
                  </p>
                </div>

                {/* Run now */}
                <button
                  onClick={(e) => { e.stopPropagation(); void handleTrigger(job.id); }}
                  disabled={triggerJob.isPending}
                  className="flex items-center gap-1.5 text-xs font-medium text-ds-text-secondary border border-ds-border rounded-lg px-3 py-1.5 hover:border-indigo-300 hover:text-indigo-600 hover:bg-indigo-50 transition-all disabled:opacity-50"
                  title="Run now"
                >
                  <Play className="w-3.5 h-3.5" /> Run now
                </button>

                {/* Pause / Resume */}
                <button
                  onClick={(e) => { e.stopPropagation(); void handlePause(job); }}
                  disabled={pauseJob.isPending}
                  className={`flex items-center gap-1.5 text-xs font-medium border rounded-lg px-3 py-1.5 transition-all disabled:opacity-50 ${
                    job.enabled
                      ? "text-ds-text-secondary border-ds-border hover:border-amber-300 hover:text-amber-600 hover:bg-amber-50"
                      : "text-emerald-600 border-emerald-200 bg-emerald-50 hover:bg-emerald-100"
                  }`}
                  title={job.enabled ? "Pause job" : "Resume job"}
                >
                  {job.enabled
                    ? <><Pause className="w-3.5 h-3.5" /> Pause</>
                    : <><PlayCircle className="w-3.5 h-3.5" /> Resume</>
                  }
                </button>

                {/* Edit */}
                <button
                  onClick={(e) => { e.stopPropagation(); setEditingJob(job); }}
                  className="text-ds-text-secondary hover:bg-ds-bg hover:text-ds-text rounded-lg px-2.5 py-1.5 transition-colors"
                  title="Edit"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>

                {/* Delete */}
                <button
                  onClick={(e) => { e.stopPropagation(); void handleDelete(job.id); }}
                  disabled={deleteJob.isPending}
                  className={`rounded-lg px-2.5 py-1.5 text-xs font-medium transition-all disabled:opacity-50 ${
                    confirmingDeleteId === job.id
                      ? "bg-red-100 text-red-700 border border-red-200"
                      : "text-ds-text-secondary hover:bg-red-50 hover:text-red-600"
                  }`}
                >
                  {confirmingDeleteId === job.id ? "Confirm" : <Trash2 className="w-3.5 h-3.5" />}
                </button>

                <ChevronDown className={`w-4 h-4 text-ds-text-muted transition-transform ${expandedId === job.id ? "rotate-180" : ""}`} />
              </button>

              {expandedId === job.id && <JobRunHistory jobId={job.id} />}
            </div>
          ))}
        </div>
      )}

      {createOpen  && <JobFormDialog onClose={() => setCreateOpen(false)} />}
      {editingJob  && <JobFormDialog job={editingJob} onClose={() => setEditingJob(null)} />}
    </div>
  );
}

// ── Run history (expanded) ────────────────────────────────────────────────────

function JobRunHistory({ jobId }: { jobId: string }) {
  const { data: job, isLoading } = useScheduledJob(jobId);
  if (isLoading || !job) return <div className="px-5 pb-4 text-xs text-ds-text-muted">Loading run history…</div>;
  return (
    <div className="px-5 pb-4 border-t border-ds-border pt-3">
      <p className="text-xs font-semibold text-ds-text-secondary mb-2">Recent runs</p>
      {job.runs.length === 0 ? (
        <p className="text-xs text-ds-text-muted">Never run yet.</p>
      ) : (
        <div className="space-y-1.5">
          {job.runs.map((run) => (
            <div key={run.id} className="flex items-center gap-3 text-xs">
              <span className={`font-semibold px-2 py-0.5 rounded-full border ${STATUS_STYLE[run.status]}`}>
                {run.status}
              </span>
              <span className="text-ds-text-muted">{run.triggeredBy}</span>
              <span className="text-ds-text-muted">{formatDate(run.startedAt)}</span>
              {run.resultMessage && <span className="text-ds-text-secondary truncate">{run.resultMessage}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Create / Edit dialog ──────────────────────────────────────────────────────

function toDatetimeLocalValue(isoString: string): string {
  const date = new Date(isoString);
  const offsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

function JobFormDialog({ job, onClose }: { job?: SchedulerJob & { runs?: unknown }; onClose: () => void }) {
  const { toast }      = useToast();
  const createJob      = useCreateScheduledJob();
  const updateJob      = useUpdateScheduledJob();
  const { data: files }        = useFiles();
  const { data: enterprises }  = usePlatformEnterprises();
  const user = useAuthStore((s) => s.user);
  const isPlatformAdmin = user?.platformRole === "platform_admin";
  const isEditing = Boolean(job);

  // Resolve which app a saved job belongs to (from its webhook URL)
  const savedApp = job
    ? Object.entries(APP_REGISTRY).find(([, v]) => v.webhookUrl === job.webhookUrl)?.[0] ?? ""
    : "";

  // Form state
  const [name, setName]               = useState(job?.name ?? "");
  const [description, setDescription] = useState(job?.description ?? "");
  const [jobType, setJobType]         = useState<SchedulerJob["jobType"]>(job?.jobType ?? "etl");
  const [appKey, setAppKey]           = useState(savedApp || Object.keys(APP_REGISTRY)[0] || "");
  const [triggerType, setTriggerType] = useState<SchedulerJob["triggerType"]>(job?.triggerType ?? "cron");
  const [cronExpression, setCron]     = useState(
    job?.triggerType === "cron" ? (job.triggerConfig.expression ?? "0 3 * * *") : "0 3 * * *",
  );
  const [intervalMinutes, setInterval] = useState(
    job?.triggerType === "interval" ? String((job.triggerConfig.seconds ?? 3600) / 60) : "60",
  );
  const [oneTimeAt, setOneTimeAt]     = useState(
    job?.triggerType === "one_time" && job.triggerConfig.at ? toDatetimeLocalValue(job.triggerConfig.at) : "",
  );
  const savedJobConfig = (job?.jobConfig ?? {}) as Record<string, string>;
  const [targetTable, setTargetTable]     = useState<string>(savedJobConfig["targetTable"]  ?? "");
  const [sourceFileId, setSourceFileId]   = useState<string>(savedJobConfig["sourceFileId"] ?? "");
  const [enterpriseId, setEnterpriseId]   = useState<string>(savedJobConfig["enterpriseId"] ?? "");
  const [notificationType, setNotify] = useState<SchedulerJob["notificationType"]>(job?.notificationType ?? "bell");
  const [enabled, setEnabled]         = useState(job?.enabled ?? true);
  const [error, setError]             = useState("");

  const app = APP_REGISTRY[appKey];
  const allFiles = files?.files ?? [];

  const handleSubmit = async () => {
    setError("");
    if (!name.trim()) { setError("Name is required."); return; }
    if (!appKey || !app) { setError("Select an application."); return; }
    if (jobType === "etl" && !targetTable) { setError("Select a target table."); return; }
    if (jobType === "etl" && !sourceFileId) { setError("Select a source file."); return; }
    if (triggerType === "one_time" && !oneTimeAt) { setError("Pick a date and time for a one-time trigger."); return; }

    const triggerConfig =
      triggerType === "cron"     ? { expression: cronExpression }
      : triggerType === "interval" ? { seconds: Number(intervalMinutes) * 60 }
      : { at: new Date(oneTimeAt).toISOString() };

    const jobConfig: Record<string, unknown> =
      jobType === "etl"
        ? {
            targetTable,
            sourceFileId,
            ...(isPlatformAdmin && enterpriseId ? { enterpriseId } : {}),
          }
        : {};

    try {
      if (isEditing) {
        await updateJob.mutateAsync({
          id: job!.id,
          name: name.trim(),
          description: description.trim() || null,
          triggerType, triggerConfig,
          webhookUrl: app.webhookUrl,
          jobConfig, notificationType, enabled,
        });
        toast({ title: "Job updated", variant: "success" });
      } else {
        await createJob.mutateAsync({
          name: name.trim(),
          description: description.trim() || undefined,
          jobType, triggerType, triggerConfig,
          webhookUrl: app.webhookUrl,
          jobConfig, notificationType,
        });
        toast({ title: "Job created", variant: "success" });
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${isEditing ? "update" : "create"} job`);
    }
  };

  const selCls = "w-full h-10 rounded-lg border border-ds-border px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-card shadow-card border border-ds-border p-6 w-full max-w-lg space-y-4 max-h-[90vh] overflow-y-auto">
        <div>
          <h2 className="text-sm font-semibold text-ds-text">{isEditing ? "Edit scheduled job" : "New scheduled job"}</h2>
          <p className="text-xs text-ds-text-muted mt-1">
            Platform fires this on schedule; the selected application does the actual work.
          </p>
        </div>

        <div className="space-y-3">
          {/* Name + description */}
          <div className="space-y-1">
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nightly payroll ETL" />
          </div>
          <div className="space-y-1">
            <Label>Description (optional)</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>

          {/* Job type + notification */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Job type</Label>
              <select value={jobType}
                onChange={(e) => { setJobType(e.target.value as SchedulerJob["jobType"]); setTargetTable(""); setSourceFileId(""); }}
                disabled={isEditing} className={selCls}>
                <option value="etl">ETL</option>
                <option value="report_generation">Report generation</option>
              </select>
              {isEditing && <p className="text-xs text-ds-text-muted">Fixed once created.</p>}
            </div>
            <div className="space-y-1">
              <Label>Notify via</Label>
              <select value={notificationType} onChange={(e) => setNotify(e.target.value as SchedulerJob["notificationType"])} className={selCls}>
                <option value="bell">Bell</option>
                <option value="email">Email</option>
                <option value="both">Both</option>
                <option value="none">None</option>
              </select>
            </div>
          </div>

          {/* Application */}
          <div className="space-y-1">
            <Label>Application</Label>
            <select value={appKey} onChange={(e) => { setAppKey(e.target.value); setTargetTable(""); }} className={selCls}>
              {Object.entries(APP_REGISTRY).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
            <p className="text-xs text-ds-text-muted">The application that will handle this job.</p>
          </div>

          {/* ETL-specific fields */}
          {jobType === "etl" && (
            <>
              {/* Target table */}
              <div className="space-y-1">
                <Label>Target table</Label>
                <select value={targetTable} onChange={(e) => setTargetTable(e.target.value)} className={selCls}>
                  <option value="">Select table…</option>
                  {(app?.etlTables ?? []).map(t => (
                    <option key={t} value={t}>{TABLE_LABELS[t] ?? t}</option>
                  ))}
                </select>
              </div>

              {/* Source file */}
              <div className="space-y-1">
                <Label>Source file</Label>
                <select value={sourceFileId} onChange={(e) => setSourceFileId(e.target.value)} className={selCls}>
                  <option value="">Select file…</option>
                  {(allFiles as { id: string; fileName: string }[]).map((f) => (
                    <option key={f.id} value={f.id}>{f.fileName}</option>
                  ))}
                </select>
                {allFiles.length === 0 && (
                  <p className="text-xs text-amber-600">No files found. Upload a CSV in My Files first.</p>
                )}
              </div>

              {/* Enterprise (platform admins only) */}
              {isPlatformAdmin && (
                <div className="space-y-1">
                  <Label>Enterprise</Label>
                  <select value={enterpriseId} onChange={(e) => setEnterpriseId(e.target.value)} className={selCls}>
                    <option value="">Auto (from token)</option>
                    {((enterprises as { id: string; name: string }[]) ?? []).map((e) => (
                      <option key={e.id} value={e.id}>{e.name}</option>
                    ))}
                  </select>
                  <p className="text-xs text-ds-text-muted">
                    Leave blank to use the token’s enterprise context. Select explicitly to load into a specific enterprise.
                  </p>
                </div>
              )}
            </>
          )}

          {/* Trigger type */}
          <div className="space-y-1">
            <Label>Trigger</Label>
            <select value={triggerType} onChange={(e) => setTriggerType(e.target.value as SchedulerJob["triggerType"])} className={selCls}>
              <option value="cron">Cron schedule</option>
              <option value="interval">Every N minutes</option>
              <option value="one_time">One-time</option>
            </select>
          </div>
          {triggerType === "cron" && (
            <div className="space-y-1">
              <Label>Cron expression</Label>
              <Input value={cronExpression} onChange={(e) => setCron(e.target.value)} placeholder="0 3 * * *" />
              <p className="text-xs text-ds-text-muted">Standard 5-field cron, evaluated in UTC.</p>
            </div>
          )}
          {triggerType === "interval" && (
            <div className="space-y-1">
              <Label>Every (minutes)</Label>
              <Input type="number" min={1} value={intervalMinutes} onChange={(e) => setInterval(e.target.value)} />
            </div>
          )}
          {triggerType === "one_time" && (
            <div className="space-y-1">
              <Label>Run at</Label>
              <Input type="datetime-local" value={oneTimeAt} onChange={(e) => setOneTimeAt(e.target.value)} />
            </div>
          )}

          {/* Enabled toggle (edit only) */}
          {isEditing && (
            <label className="flex items-center gap-2 text-sm text-ds-text cursor-pointer">
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
              Enabled
            </label>
          )}

          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>

        <div className="flex gap-2 justify-end pt-1">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} loading={isEditing ? updateJob.isPending : createJob.isPending}>
            {isEditing ? "Save changes" : "Create job"}
          </Button>
        </div>
      </div>
    </div>
  );
}
